import React, {useState, useEffect, useRef} from 'react'
import axios from 'axios'
import {useReduxAuth} from '../../../auth'
import {useParams} from 'react-router-dom'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {toast} from 'react-toastify'
import ManualTimeTrackedForm from '../forms/ManualTimeTrackedForm'
import moment from 'moment'
import {FiEdit, FiPlus} from 'react-icons/fi'
import {useTimeTrack} from '../../context/TimeTrackedContext'
import PlayIcon from '../common/icons/PlayIcon'
import PauseIcon from '../common/icons/PauseIcon'
import NotesModal from '../modals/NotesModal'
import {getTodayDateTime, isUserSubscribed} from '../../helper'
import {isPatientActive} from '../../../../utitlities/patientStatus'
// Redux imports for refreshing patient data after manual time entry
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../../../../../store/store'
import { refreshPatientAfterDeviceChange } from '../../../../../store/slices/patientSlice'
import {USER_ROLES} from '../../../../constants/StatusConstants'
import {useMADoctorDropdown} from '../../context/MADoctorDropdownContext'

interface TimerComponentProps {
  patientData: any
  onEntryAdded: () => void
}

const TimerComponent: React.FC<TimerComponentProps> = ({patientData, onEntryAdded}) => {
  const notifyError = (msg: string) => toast.error(msg)
  const notifySuccess = (msg: string) => toast.success(msg)
  const active = isPatientActive(patientData)
  const dispatch = useDispatch<AppDispatch>()

  // time-track context
  const {timeTrackedData, setTimeTrackedData} = useTimeTrack()

  const {patientId} = useParams()
  const {currentUser} = useReduxAuth()
  const roleId = currentUser?.role_id
  
  // MA Doctor Dropdown
  const maDropdown = useMADoctorDropdown()
  const maSelectedDoctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.selectedDoctorId : undefined

  const timerRef = useRef<HTMLDivElement | null>(null)
  const currentTaskRef = useRef<any | null>(null)
  const noteInputRef = useRef<HTMLInputElement | null>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const storageKey = `timerState-${patientId}`

  const [currentTask, setCurrentTask] = useState<any>(() => {
    const savedTask = localStorage.getItem(storageKey)
    return savedTask
      ? JSON.parse(savedTask)
      : {
          description: '',
          elapsedTime: 0,
          startTime: '',
          endTime: '',
          isRunning: false,
        }
  })

  const [showNotesField, setShowNotesField] = useState(false)

  const [showManualTimerModal, setShowManualTimerModal] = useState(false)
  const [showNotesModal, setShowNotesModal] = useState(false)
  const [tempNotes, setTempNotes] = useState('')

  // Check if user has valid subscription to access premium features
  const hasValidSubscription = isUserSubscribed(currentUser)

  useEffect(() => {
    currentTaskRef.current = currentTask
  }, [currentTask])

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(currentTask))
  }, [currentTask, storageKey])

  const formatTime = (seconds: number): string => {
    const h = String(Math.floor(seconds / 3600)).padStart(2, '0')
    const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0')
    const s = String(seconds % 60).padStart(2, '0')
    return `${h}:${m}:${s}`
  }

  const getLocalDateTime = (): string => new Date().toISOString().slice(0, 19)

  const startTimer = () => {
    if (!active) {
      return
    }
    if (!hasValidSubscription) {
      return
    }
    setCurrentTask((prev: any) => {
      const existingStartTime = prev.startTime || getLocalDateTime()
      const interval = setInterval(() => {
        setCurrentTask((prev: any) => ({
          ...prev,
          elapsedTime: prev.elapsedTime + 1,
        }))
      }, 1000)

      intervalRef.current = interval

      return {
        ...prev,
        startTime: existingStartTime, // 👈 Preserve the startTime
        isRunning: true,
      }
    })
  }

  const pauseTimer = () => {
    if (!active) {
      notifyError('Patient is inactive')
      return
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
      setCurrentTask((prev: any) => ({...prev, isRunning: false}))
    }
  }

  const resumeTimer = () => {
    if (!active) {
      notifyError('Patient is inactive')
      return
    }
    if (!hasValidSubscription) {
      return
    }
    if (!currentTaskRef.current.isRunning && patientId) {
      startSubTimer()
    }
  }

  const stopTimer = async () => {
    if (!active) {
      notifyError('Patient is inactive')
      return
    }
    
    // Don't save if user doesn't have valid subscription
    if (!hasValidSubscription) {
      return
    }
    
    const task = currentTaskRef.current

    // Don't save if timer never ran or has <= 2 seconds elapsed time
    if (!task || task.elapsedTime <= 2) {
      clearInterval(intervalRef.current as NodeJS.Timeout)
      intervalRef.current = null
      setCurrentTask({
        description: '',
        elapsedTime: 0,
        startTime: '',
        endTime: '',
        isRunning: false,
      })
      localStorage.removeItem(storageKey) 
      return
    }
    
    clearInterval(intervalRef.current as NodeJS.Timeout)

    const actualElapsedTimeInSeconds = task.elapsedTime
    const endTime = moment() // local time moment
    const startTime = moment(endTime).subtract(actualElapsedTimeInSeconds, 'seconds')

    try {
      await axios.post(API_ENDPOINTS.CREATE_TIME_TRACKED_ENTRY(currentUser?.id, patientId), {
        from: startTime,
        to: endTime,
        time_spent: formatTime(actualElapsedTimeInSeconds),
        description: task.description,
      })

      setCurrentTask({
        description: '',
        elapsedTime: 0,
        startTime: '',
        endTime: '',
        isRunning: false,
      })
      onEntryAdded()
    } catch (error: any) {
      localStorage.getItem('kt-auth-react-v') && notifyError(error?.response?.data?.message)
    } finally {
      localStorage.removeItem(storageKey)
    }
  }

  useEffect(() => {
    if (!active) return
    
    // Only start timer if user has valid subscription
    if (hasValidSubscription) {
      startSubTimer()
    }
    
    if (!document.hasFocus() || document.visibilityState !== 'visible') {
      pauseTimer()
    }

    const handleBlur = () => {
      pauseTimer()
    }

    const handleFocus = () => {
      const storedTask = localStorage.getItem(storageKey)
      if (storedTask) {
        const parsed = JSON.parse(storedTask)
        // Only resume if this timer belongs to current patient and user has subscription
        if (parsed.isRunning === false && patientId && hasValidSubscription) {
          resumeTimer()
        }
      }
    }

    window.addEventListener('blur', handleBlur)
    window.addEventListener('focus', handleFocus)

    return () => {
      // Only attempt to stop timer if user has subscription
      if (hasValidSubscription) {
        stopTimer()
      } else {
        // Just clean up without saving
        clearInterval(intervalRef.current as NodeJS.Timeout)
        localStorage.removeItem(storageKey)
      }
      window.removeEventListener('blur', handleBlur)
      window.removeEventListener('focus', handleFocus)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasValidSubscription])

  const handleSubmit = async (values: any, resetForm: () => void) => {
    const seconds = (new Date(values.to).getTime() - new Date(values.from).getTime()) / 1000
    const duration = moment.duration(seconds, 'seconds')
    const time_spent = moment.utc(duration.asMilliseconds()).format('HH:mm:ss')

    const newEntry = {
      from: values.from,
      to: values.to,
      time_spent,
      description: values.description,
      tracking_type: 'manual',
    }

    // Backup current state
    const previousEntries = [...timeTrackedData]

    try {
      // Optimistically add new entry to UI
      const tempId = Date.now() // Temporary ID for UI rendering
      const optimisticEntry = {
        ...newEntry,
        id: tempId,
        uuid: crypto.randomUUID(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        patient_id: patientId,
        tracked_by: currentUser?.id,
        TrackedBy: {
          id: currentUser?.id,
          full_name: currentUser?.full_name,
        },
      }

      setTimeTrackedData((prev) => [optimisticEntry, ...prev])

      // API call
      const response = await axios.post(
        API_ENDPOINTS.CREATE_TIME_TRACKED_ENTRY(currentUser?.id, patientId, getTodayDateTime),
        newEntry
      )

      notifySuccess(response?.data?.message)

      // Refresh Redux patient state to update PatientDetailBadges after manual time entry
      if (patientId) {
        const doctorIdForRefresh = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maSelectedDoctorId : undefined
        dispatch(refreshPatientAfterDeviceChange({ patientUuid: patientId, doctorId: doctorIdForRefresh }))
      }

      // Replace the optimistic entry with real one from server
      const savedEntry = response.data?.data?.timeTracked

      // 4. Merge TrackedBy manually, replace optimistic entry
      setTimeTrackedData((prev) =>
        prev.map((entry) =>
          entry.id === tempId
            ? {
                ...savedEntry,
                TrackedBy: {
                  id: currentUser?.id,
                  full_name: currentUser?.full_name,
                },
              }
            : entry
        )
      )
    } catch (error: any) {
      notifyError(error?.response?.data?.message)

      // Revert to original state
      setTimeTrackedData(previousEntries)
    } finally {
      resetForm()
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showNotesField &&
        noteInputRef.current &&
        !noteInputRef.current.contains(event.target as Node)
      ) {
        setShowNotesField(false)
      }
    }
    if (showNotesField) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showNotesField])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setShowNotesField(false)
    }
  }

  // Open notes modal and initialize with current notes
  const handleOpenNotesModal = () => {
    if (!active) {
      return
    }
    setTempNotes(currentTask.description)
    setShowNotesModal(true)
  }
  
  const startSubTimer = () => {
    if (hasValidSubscription) {
      startTimer()   
    }
  }

  // Save notes from modal to current task
  const handleSaveNotes = () => {
    setCurrentTask({...currentTask, description: tempNotes})
  }

  return (
    <>
      <div className='timer-container'>
        <div className={`timer-wrapper ${(!active || !hasValidSubscription) ? 'bg-light border-secondary text-muted opacity-50 cursor-disabled' : ''}`}>
          <div
            className='icon-cell-container'
            onClick={handleOpenNotesModal}
            style={{position: 'relative', cursor: 'pointer'}}
          >
            {hasValidSubscription &&
              <div className={`icon-cell ${currentTask.description ? 'has-notes' : ''} ${!active ? 'bg-light border-end border-dark text-muted opacity-50 cursor-disabled' : ''
                }`}>
              <FiEdit size={14} />
            </div>}
            {currentTask.description && (
              <span
                className='note-indicator'
                style={{
                  position: 'absolute',
                  top: '0',
                  right: '0',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#F1416C', // Red color
                  border: '1px solid white',
                }}
              />
            )}
          </div>

          <div ref={timerRef} className='time-text'>
            {formatTime(currentTask.elapsedTime)}
          </div>

          {currentTask.isRunning ? (
            <PauseIcon color='--kt-primary' size='22' onClick={pauseTimer} />
          ) : (
            <PlayIcon color='--kt-primary' size='22' onClick={startSubTimer} />
          )}
        </div>

        <button
          className={`btn-with-icon ${(!active || !hasValidSubscription) ? 'bg-light border border-secondary text-muted opacity-50 cursor-disabled' : ''}`}
          disabled={!hasValidSubscription}
          onClick={() => {
            if (!active) return
            setShowManualTimerModal(true)
          }}
        >
          {' '}
          <div className={`plus-icon-box ${(!active || !hasValidSubscription) ? 'bg-secondary text-dark' : ''}`}>
            <FiPlus />
          </div>
          Time
        </button>
      </div>

      {/* Use the separate notes modal component */}
      <NotesModal
        show={showNotesModal}
        onHide={() => setShowNotesModal(false)}
        notes={tempNotes}
        onNotesChange={setTempNotes}
        onSave={handleSaveNotes}
      />

      <ManualTimeTrackedForm
        show={showManualTimerModal}
        onHide={() => setShowManualTimerModal(false)}
        onSubmit={handleSubmit}
      />
    </>
  )
}

export default TimerComponent
